import type {
  GridOptions,
  GridReadyEvent,
  StateUpdatedEvent,
} from "ag-grid-community";
import type { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Centered } from "@/components/ui/container";
import { Loader } from "@/components/ui/loader";
import { useShallowStable } from "@/utils/use-shallow-stable";

function GridLoadingOverlay() {
  return (
    <Centered>
      <Loader size="medium" />
    </Centered>
  );
}

const DRAG_OPTIONS = {
  rowDragManaged: true,
  rowDragMultiRow: true,
  suppressMoveWhenRowDragging: true,
} as const satisfies Partial<GridOptions>;

const STATIC_SIDE_BAR: GridOptions["sideBar"] = { toolPanels: ["filters"] };

type AdminGridOptionsExtras = {
  initialFilterText?: string;
  onFilterTextChange?: (value: string) => void;
};

export function useAdminGridOptions<T>(
  rowData: T[] | null | undefined,
  columnDefs: GridOptions<T>["columnDefs"],
  otherOptions: GridOptions<T> = {},
  extras: AdminGridOptionsExtras = {},
) {
  const ref = useRef<AgGridReact<T>>(null);
  const stableOptions = useShallowStable(
    otherOptions as Record<string, unknown>,
  ) as GridOptions<T>;

  const { filterText, filterTextProps } = useQuickFilter(
    extras.initialFilterText,
    extras.onFilterTextChange,
  );

  const quickFilterText =
    stableOptions.quickFilterText !== undefined
      ? stableOptions.quickFilterText
      : filterText;

  const userOnGridReady = stableOptions.onGridReady;
  const onGridReady = useCallback(
    (event: GridReadyEvent<T>) => {
      userOnGridReady?.(event);
    },
    [userOnGridReady],
  );

  const options = useMemo<GridOptions<T>>(() => {
    const { quickFilterText: _callerQuickFilter, ...rest } = stableOptions;
    const dragOptions = rest.onRowDragEnd ? DRAG_OPTIONS : {};

    return {
      rowData,
      columnDefs,
      suppressScrollOnNewData: true,
      animateRows: true,
      suppressColumnMoveAnimation: true,
      sideBar: STATIC_SIDE_BAR,
      quickFilterText,
      maintainColumnOrder: false,
      loadingOverlayComponent: GridLoadingOverlay,
      ...dragOptions,
      ...rest,
      onGridReady,
    };
  }, [rowData, columnDefs, stableOptions, quickFilterText, onGridReady]);

  return { ref, options, filterTextProps };
}

export function usePersistedAdminGridOptions<T>(
  name: string | undefined,
  rowData: T[] | null | undefined,
  columnDefs: GridOptions<T>["columnDefs"],
  otherOptions: GridOptions<T> = {},
) {
  const stateName = `${name}-state`;
  const filterName = `${name}-filter`;

  const initialState = useMemo(() => {
    const initialStateStr = sessionStorage.getItem(stateName);
    if (!initialStateStr) return undefined;
    try {
      return JSON.parse(initialStateStr);
    } catch {
      sessionStorage.removeItem(stateName);
      return undefined;
    }
  }, [stateName]);

  const initialFilter = useMemo(
    () => sessionStorage.getItem(filterName) ?? "",
    [filterName],
  );

  const persistFilter = useCallback(
    (value: string) => {
      sessionStorage.setItem(filterName, value);
    },
    [filterName],
  );

  const { ref, options, filterTextProps } = useAdminGridOptions(
    rowData,
    columnDefs,
    otherOptions,
    { initialFilterText: initialFilter, onFilterTextChange: persistFilter },
  );

  const onStateUpdated = useCallback(
    (event: StateUpdatedEvent<T>) => {
      sessionStorage.setItem(stateName, JSON.stringify(event.state));
    },
    [stateName],
  );

  const persistedOptions = useMemo<GridOptions<T>>(
    () => ({
      ...options,
      initialState,
      maintainColumnOrder: true,
      onStateUpdated,
    }),
    [options, initialState, onStateUpdated],
  );

  return {
    ref,
    filterTextProps,
    options: persistedOptions,
  };
}

function useQuickFilter(initial?: string, onChange?: (value: string) => void) {
  const [filterText, setFilterText] = useState(initial);

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const handleChange = useCallback((value: string) => {
    setFilterText(value);
    onChangeRef.current?.(value);
  }, []);

  const filterTextProps = useMemo(
    () => ({
      "aria-label": "Table search",
      value: filterText ?? "",
      onChange: handleChange,
      placeholder: "Search...",
    }),
    [filterText, handleChange],
  );

  return { filterTextProps, setFilterText, filterText: filterText ?? "" };
}
